import {
  Artwork,
  Player,
  PlayerConnectionStatus,
  PlayerGameStatus,
  QuestionType,
  Round,
  SessionData,
  SessionStatus
} from '@/API';
import { Reaction as PlayerReaction } from '@/components/Controls/ReactionsBar';
import MultiplayerDemoContext from '@/contexts/MultiplayerDemoContext';
import { PlayerSessionContext } from '@/contexts/PlayerSessionContext';
import { sendZoomMessage, subscribeToZoomMessages } from '@/integrations/zoom';
import { useZoom } from '@/providers/ZoomProvider';
import { Reaction as ReactionOption, ReactionType } from '@/utils/reactions';
import {
  asZoomPayload,
  DrawingAssembler,
  drawingChunkMessages,
  isMultiplayerMessage,
  MAX_PLAYERS,
  MAX_DRAWING_CHARACTERS,
  messageToState,
  MULTIPLAYER_PROMPT,
  normalizeDisplayName,
  randomMultiplayerId,
  stateToMessage,
  type MultiplayerMessage,
  type MultiplayerPlayer,
  type MultiplayerPlayerStatus,
  type MultiplayerState
} from '@/zoom/multiplayerProtocol';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

type ProviderMode = 'host' | 'guest';

type Props = {
  children: ReactNode;
  mode: ProviderMode;
  guestGameId?: string;
  guestHostId?: string;
};

const toPlayerStatus = (status: MultiplayerPlayerStatus): PlayerGameStatus => {
  const values: Record<MultiplayerPlayerStatus, PlayerGameStatus> = {
    ready: PlayerGameStatus.READY,
    drawing: PlayerGameStatus.DRAWING,
    submitted: PlayerGameStatus.SUBMITTED,
    sharing: PlayerGameStatus.SHARING,
    shared: PlayerGameStatus.SHARED
  };
  return values[status];
};

const toSessionStatus = (phase: MultiplayerState['phase']): SessionStatus => {
  if (phase === 'lobby') return SessionStatus.created;
  if (phase === 'drawing') return SessionStatus.drawing;
  if (phase === 'sharing') return SessionStatus.sharing;
  return SessionStatus.ended;
};

const apiPlayer = (player: MultiplayerPlayer): Player =>
  ({
    __typename: 'Player',
    id: player.id,
    name: player.name,
    gameStatus: toPlayerStatus(player.status),
    connectionStatus: PlayerConnectionStatus.CONNECTED,
    sessionId: 'zoom-multiplayer'
  }) as Player;

const canUseMessaging = (zoom: ReturnType<typeof useZoom>) =>
  zoom.embedded &&
  zoom.inMeeting &&
  !!zoom.userContext?.participantUUID &&
  zoom.supports('getUserContext') &&
  zoom.supports('sendMessage') &&
  zoom.supports('onMessage');

export default function ZoomMultiplayerProvider({
  children,
  mode,
  guestGameId,
  guestHostId
}: Props) {
  const zoom = useZoom();
  const user = zoom.userContext;
  const currentPlayerId = user?.participantUUID ?? '';
  const displayName = normalizeDisplayName(user?.screenName);
  const requestedHost = mode === 'host';
  const isHost = requestedHost && user?.role === 'host';
  const available = canUseMessaging(zoom);

  const [game, setGame] = useState<MultiplayerState | null>(null);
  const gameRef = useRef<MultiplayerState | null>(null);
  const [drawings, setDrawings] = useState<Record<string, string>>({});
  const drawingsRef = useRef<Record<string, string>>({});
  const [reactions, setReactions] = useState<PlayerReaction[]>([]);
  const assemblerRef = useRef(new DrawingAssembler());
  const [error, setError] = useState<string | null>(null);

  const replaceGame = useCallback((next: MultiplayerState) => {
    gameRef.current = next;
    setGame(next);
  }, []);

  const replaceDrawings = useCallback((next: Record<string, string>) => {
    drawingsRef.current = next;
    setDrawings(next);
  }, []);

  const send = useCallback(async (message: MultiplayerMessage) => {
    return sendZoomMessage(asZoomPayload(message));
  }, []);

  const broadcastState = useCallback(
    async (state = gameRef.current) => {
      if (!state || !currentPlayerId) return;
      await send(stateToMessage(state, currentPlayerId));
    },
    [currentPlayerId, send]
  );

  const announce = useCallback(async () => {
    const state = gameRef.current;
    if (!state || !currentPlayerId || state.phase === 'ended') return;
    await send({
      v: 1,
      t: 'announce',
      f: currentPlayerId,
      g: state.gameId,
      h: state.hostId,
      p: state.phase
    });
  }, [currentPlayerId, send]);

  const commitHostState = useCallback(
    (update: (state: MultiplayerState) => MultiplayerState) => {
      if (!isHost || !gameRef.current) return null;
      const next = { ...update(gameRef.current), revision: gameRef.current.revision + 1 };
      replaceGame(next);
      void broadcastState(next);
      return next;
    },
    [isHost, replaceGame, broadcastState]
  );

  const sendDrawing = useCallback(
    async (ownerId: string, drawing: string) => {
      const state = gameRef.current;
      if (!state || !currentPlayerId) return false;
      if (drawing.length > MAX_DRAWING_CHARACTERS) {
        setError('This drawing is too detailed for Zoom messaging. Undo a few strokes and retry.');
        return false;
      }

      const chunks = drawingChunkMessages(state.gameId, currentPlayerId, ownerId, drawing);
      for (const chunk of chunks) {
        let sent = false;
        for (let attempt = 0; attempt < 3 && !sent; attempt += 1) {
          sent = await send(chunk);
          if (!sent) await new Promise((resolve) => setTimeout(resolve, 150 * (attempt + 1)));
        }
        if (!sent) {
          setError('The drawing was not sent. Keep it open and press Submit again.');
          return false;
        }
      }
      setError(null);
      return true;
    },
    [currentPlayerId, send]
  );

  const sendCurrentArtwork = useCallback(async () => {
    const state = gameRef.current;
    const ownerId = state?.sharingPlayerId;
    if (!state || !ownerId) return;
    const drawing = drawingsRef.current[ownerId];
    if (drawing) await sendDrawing(ownerId, drawing);
  }, [sendDrawing]);

  // Initialize the host or guest projection once Zoom has supplied meeting identity.
  useEffect(() => {
    if (!available || !currentPlayerId || gameRef.current) return;

    if (isHost) {
      replaceGame({
        gameId: randomMultiplayerId('game'),
        hostId: currentPlayerId,
        revision: 1,
        phase: 'lobby',
        prompt: MULTIPLAYER_PROMPT,
        players: [{ id: currentPlayerId, name: displayName, status: 'ready' }],
        sharingPlayerId: null
      });
      return;
    }

    if (mode === 'guest' && guestGameId && guestHostId) {
      replaceGame({
        gameId: guestGameId,
        hostId: guestHostId,
        revision: 0,
        phase: 'lobby',
        prompt: MULTIPLAYER_PROMPT,
        players: [],
        sharingPlayerId: null
      });
    }
  }, [
    available,
    currentPlayerId,
    displayName,
    isHost,
    mode,
    guestGameId,
    guestHostId,
    replaceGame
  ]);

  const handleMessage = useCallback(
    async (message: MultiplayerMessage) => {
      const state = gameRef.current;
      if (!state || message.f === currentPlayerId) return;

      if (isHost && message.t === 'discover') {
        await announce();
        return;
      }

      if ('g' in message && message.g !== state.gameId) return;

      if (isHost && message.t === 'join') {
        const existing = state.players.some((player) => player.id === message.f);
        if (!existing && state.phase !== 'ended' && state.players.length < MAX_PLAYERS) {
          const status: MultiplayerPlayerStatus =
            state.phase === 'drawing' ? 'drawing' : state.phase === 'lobby' ? 'ready' : 'ready';
          commitHostState((current) => ({
            ...current,
            players: [
              ...current.players,
              { id: message.f, name: normalizeDisplayName(message.n), status }
            ]
          }));
          if (state.phase === 'sharing') await sendCurrentArtwork();
        } else {
          if (state.phase === 'sharing') await sendCurrentArtwork();
          await broadcastState();
        }
        return;
      }

      if (isHost && message.t === 'request') {
        if (state.phase === 'sharing') await sendCurrentArtwork();
        await broadcastState();
        return;
      }

      if (!isHost && message.t === 'state') {
        if (message.f !== state.hostId || message.r < state.revision) return;
        replaceGame(messageToState(message));
        return;
      }

      if (message.t === 'chunk') {
        // Guests accept rebroadcast artwork only from the known host. The host accepts
        // submissions only from a known player, during drawing, when sender and artwork
        // owner match.
        if (
          isHost &&
          (state.phase !== 'drawing' ||
            message.f !== message.o ||
            !state.players.some((player) => player.id === message.f))
        ) {
          return;
        }
        if (!isHost && message.f !== state.hostId) return;

        const complete = assemblerRef.current.add(message);
        if (!complete) return;

        replaceDrawings({ ...drawingsRef.current, [complete.ownerId]: complete.drawing });

        if (isHost && state.phase === 'drawing') {
          commitHostState((current) => ({
            ...current,
            players: current.players.map((player) =>
              player.id === complete.ownerId ? { ...player, status: 'submitted' } : player
            )
          }));
        }
        return;
      }

      if (isHost && message.t === 'reaction') {
        const allowedReaction = Object.values(ReactionType).includes(message.x as ReactionType);
        const knownPlayer = state.players.some((player) => player.id === message.f);
        if (
          state.phase !== 'sharing' ||
          message.a !== message.f ||
          message.a === state.sharingPlayerId ||
          !knownPlayer ||
          !allowedReaction
        ) {
          return;
        }

        // Re-broadcast accepted reactions. Each receiver exposes only its own reaction
        // to PlayerSpotlight so another participant cannot lock its picker.
        await send({ ...message, f: currentPlayerId });
        return;
      }

      if (!isHost && message.t === 'reaction' && message.f === state.hostId) {
        // The sender ID is carried in `a`; `f` is the host rebroadcast authority.
        if (message.a === currentPlayerId) {
          setReactions([
            {
              playerId: currentPlayerId,
              playerCardId: `artwork-${state.sharingPlayerId}`,
              reactionType: message.x as ReactionType,
              displayed: false
            }
          ]);
        }
      }
    },
    [
      currentPlayerId,
      isHost,
      announce,
      broadcastState,
      sendCurrentArtwork,
      commitHostState,
      replaceGame,
      replaceDrawings,
      send
    ]
  );

  const handlerRef = useRef(handleMessage);
  handlerRef.current = handleMessage;

  // Register before announcing/joining so immediate replies cannot be missed.
  useEffect(() => {
    if (!available || !currentPlayerId || !game) return;

    let teardown: (() => void) | undefined;
    let interval: ReturnType<typeof setInterval> | undefined;
    let cancelled = false;

    subscribeToZoomMessages((event) => {
      if (!isMultiplayerMessage(event.payload)) return;
      // Host-authoritative multiplayer is enabled only when Zoom identifies the
      // transport sender. The SDK marks this field optional for other contexts; in
      // meeting desktop messaging it must be present or claimed host IDs are unsafe.
      if (!event.participantUUID || event.participantUUID !== event.payload.f) return;
      void handlerRef.current(event.payload);
    }).then((remove) => {
      if (cancelled) {
        remove();
        return;
      }
      teardown = remove;

      if (isHost) {
        void announce();
        void broadcastState();
        interval = setInterval(() => void announce(), 2500);
      } else {
        const join = () => {
          const state = gameRef.current;
          if (!state) return;
          if (state.players.some((player) => player.id === currentPlayerId)) return;
          void send({ v: 1, t: 'join', f: currentPlayerId, g: state.gameId, n: displayName });
          void send({ v: 1, t: 'request', f: currentPlayerId, g: state.gameId });
        };
        join();
        interval = setInterval(join, 2500);
      }
    });

    return () => {
      cancelled = true;
      teardown?.();
      if (interval) clearInterval(interval);
    };
  }, [
    available,
    currentPlayerId,
    displayName,
    game?.gameId,
    isHost,
    announce,
    broadcastState,
    send
  ]);

  // Re-request state after Zoom resumes the guest panel.
  useEffect(() => {
    const state = gameRef.current;
    if (!state || isHost || !currentPlayerId || zoom.resumeToken === 0) return;
    void send({ v: 1, t: 'request', f: currentPlayerId, g: state.gameId });
  }, [zoom.resumeToken, isHost, currentPlayerId, send]);

  const startGame = useCallback(() => {
    if ((gameRef.current?.players.length ?? 0) < 2) return;
    commitHostState((state) => ({
      ...state,
      phase: 'drawing',
      sharingPlayerId: null,
      players: state.players.map((player) => ({ ...player, status: 'drawing' }))
    }));
  }, [commitHostState]);

  const showNextDrawing = useCallback(() => {
    const state = gameRef.current;
    if (!state || !isHost) return;

    const nextPlayer = state.players.find(
      (player) => player.status === 'submitted' && !!drawingsRef.current[player.id]
    );

    if (!nextPlayer) {
      commitHostState((current) => ({ ...current, phase: 'ended', sharingPlayerId: null }));
      return;
    }

    const next = commitHostState((current) => ({
      ...current,
      phase: 'sharing',
      sharingPlayerId: nextPlayer.id,
      players: current.players.map((player) => ({
        ...player,
        status:
          player.id === nextPlayer.id
            ? 'sharing'
            : player.status === 'sharing'
              ? 'shared'
              : player.status
      }))
    }));

    if (next) {
      // Send the artwork before a second state broadcast. Receivers also guard the
      // spotlight until reassembly finishes, so unordered delivery is safe.
      void sendDrawing(nextPlayer.id, drawingsRef.current[nextPlayer.id]).then(() =>
        broadcastState(next)
      );
    }
  }, [isHost, commitHostState, sendDrawing, broadcastState]);

  const endGame = useCallback(() => {
    commitHostState((state) => ({ ...state, phase: 'ended', sharingPlayerId: null }));
  }, [commitHostState]);

  const submitDrawing = useCallback(
    async (drawing: string) => {
      const state = gameRef.current;
      if (!state || state.phase !== 'drawing' || !currentPlayerId) return;

      if (isHost) {
        if (drawing.length > MAX_DRAWING_CHARACTERS) {
          setError(
            'This drawing is too detailed for Zoom messaging. Undo a few strokes and retry.'
          );
          throw new Error('Drawing exceeds Zoom messaging limit');
        }
        setError(null);
        replaceDrawings({ ...drawingsRef.current, [currentPlayerId]: drawing });
        commitHostState((current) => ({
          ...current,
          players: current.players.map((player) =>
            player.id === currentPlayerId ? { ...player, status: 'submitted' } : player
          )
        }));
        return;
      }

      const sent = await sendDrawing(currentPlayerId, drawing);
      if (!sent) throw new Error('Drawing transfer failed');
      const current = gameRef.current;
      if (!current) return;
      // Optimistic local status; authoritative host state follows. Read the latest
      // projection after transfer so a state message received mid-upload is not lost.
      replaceGame({
        ...current,
        players: current.players.map((player) =>
          player.id === currentPlayerId ? { ...player, status: 'submitted' } : player
        )
      });
    },
    [currentPlayerId, isHost, replaceDrawings, commitHostState, sendDrawing, replaceGame]
  );

  const reactToArtwork = useCallback(
    async (reaction: ReactionOption) => {
      const state = gameRef.current;
      if (!state?.sharingPlayerId || !currentPlayerId) return;

      setReactions([
        {
          playerId: currentPlayerId,
          playerCardId: `artwork-${state.sharingPlayerId}`,
          reactionType: reaction.value,
          displayed: false
        }
      ]);

      if (!isHost) {
        await send({
          v: 1,
          t: 'reaction',
          f: currentPlayerId,
          g: state.gameId,
          a: currentPlayerId,
          x: reaction.value
        });
      }
    },
    [currentPlayerId, isHost, send]
  );

  // Clear the current user's selected reaction whenever the spotlight changes.
  useEffect(() => setReactions([]), [game?.sharingPlayerId]);

  const players = useMemo(() => (game?.players ?? []).map(apiPlayer), [game?.players]);
  const artwork = useMemo<Artwork[]>(
    () =>
      Object.entries(drawings).map(([playerId, drawing]) => ({
        __typename: 'Artwork',
        id: `artwork-${playerId}`,
        playerId,
        drawing
      })),
    [drawings]
  );
  const currentRound = useMemo(
    () =>
      ({
        __typename: 'Round',
        id: game ? `round-${game.gameId}` : 'round-connecting',
        index: 0,
        isCurrent: true,
        isLast: true,
        promptId: '',
        artwork
      }) as Round,
    [game, artwork]
  );
  const session = useMemo(
    () =>
      game
        ? ({
            __typename: 'SessionData',
            status: toSessionStatus(game.phase),
            players,
            rounds: [currentRound],
            questionType: QuestionType.CONNECTED
          } as unknown as SessionData)
        : undefined,
    [game, players, currentRound]
  );

  const playerContext = useMemo(
    () => ({
      state: (available ? 'connected' : 'disconnected') as 'connected' | 'disconnected',
      currentRound,
      artwork,
      player: players.find((player) => player.id === currentPlayerId),
      players,
      reactions,
      session,
      submitDrawing,
      reactToArtwork,
      getArtworkReactions: async () => {},
      setupPlayer: async () => {},
      updatePlayerSentiment: async () => {},
      updatePlayerQuestion: async () => {}
    }),
    [
      available,
      currentRound,
      artwork,
      players,
      currentPlayerId,
      reactions,
      session,
      submitDrawing,
      reactToArtwork
    ]
  );

  const multiplayerContext = useMemo(
    () => ({
      available,
      isHost,
      phase: game?.phase ?? ('connecting' as const),
      players: game?.players ?? [],
      currentPlayerId,
      allSubmitted:
        (game?.players.length ?? 0) > 0 &&
        (game?.players.every((player) =>
          ['submitted', 'sharing', 'shared'].includes(player.status)
        ) ??
          false),
      hasCurrentArtwork: !!game?.sharingPlayerId && !!drawings[game.sharingPlayerId],
      hasSubmissions:
        game?.players.some((player) =>
          ['submitted', 'sharing', 'shared'].includes(player.status)
        ) ?? false,
      error:
        error ??
        (requestedHost && user && !isHost ? 'Only the Zoom meeting host can start a game.' : null),
      startGame,
      showNextDrawing,
      endGame
    }),
    [
      available,
      isHost,
      game,
      currentPlayerId,
      drawings,
      error,
      requestedHost,
      user,
      startGame,
      showNextDrawing,
      endGame
    ]
  );

  return (
    <MultiplayerDemoContext.Provider value={multiplayerContext}>
      <PlayerSessionContext.Provider value={playerContext}>
        {children}
      </PlayerSessionContext.Provider>
    </MultiplayerDemoContext.Provider>
  );
}
